import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

// Components
import logo from "../../../images/logoAndSponsor.png";
import ProgressStepper from "../../../components/ProgressStepper";
import ContinueBtn from "../../../components/ContinueBtn";

// Utils
import { generateTimeSlots, timeNow } from "./TimeScheduler.util";
import { getBoothAvailability } from "../../../services/api";

export default function TimeScheduler() {
	const { locationId } = useParams();
	const durations = [15, 30, 60];
	const { id } = useParams();
	const location = useLocation();

	// Get booth data from navigation state (from BoothLanding component)
	const preSelectedBoothId = location.state?.boothId;
	const preSelectedBooth = location.state?.preSelectedBooth;

	// Use preselected booth ID if available, otherwise fall back to URL param
	const boothId = preSelectedBoothId || id;

	// Get location data from preSelectedBooth if available (contains state for timezone)
	const boothLocationData = preSelectedBooth?.location;

	const [time, setTime] = useState("");
	const [duration, setDuration] = useState(durations[0].toString());
	const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
	const [isToday, setIsToday] = useState(true);
	const [realTimeAvailableSlots, setRealTimeAvailableSlots] = useState([]);
	const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
	const [startNowAvailable, setStartNowAvailable] = useState(true);

	const navigate = useNavigate();

	// Fetch real-time booth availability
	const fetchBoothAvailability = useCallback(async () => {
		if (!boothId || !locationId) return;

		setIsLoadingAvailability(true);
		try {
			const response = await getBoothAvailability({
				boothId: boothId,
				locationId: locationId,
				date: isToday ? 0 : 1,
				duration: duration,
			});

			// Convert 24-hour format to 12-hour format for comparison
			const convertTo12Hour = (time24) => {
				const [hour, minute] = time24.split(":");
				const hour12 =
					parseInt(hour) === 0
						? 12
						: parseInt(hour) > 12
						? parseInt(hour) - 12
						: parseInt(hour);
				const ampm = parseInt(hour) < 12 ? "am" : "pm";
				return `${hour12}:${minute} ${ampm}`;
			};

			const convertedSlots = response.availableTimeSlots.map((slot) =>
				convertTo12Hour(slot)
			);
			setRealTimeAvailableSlots(convertedSlots);

			// Check if "Start Now" time is available
			const currentTime = timeNow();
			setStartNowAvailable(convertedSlots.includes(currentTime));
		} catch (error) {
			console.error("Error fetching booth availability:", error);
			// Fallback to showing all generated slots if API fails
			// Use booth location data if available, otherwise fallback to locationId
			setRealTimeAvailableSlots(
				generateTimeSlots(duration, boothLocationData || locationId, isToday)
			);
			setStartNowAvailable(true);
		}
		setIsLoadingAvailability(false);
	}, [boothId, locationId, boothLocationData, duration, isToday]);

	// Update time slots every minute to prevent selecting past times
	useEffect(() => {
		const updateTimeSlots = () => {
			// Use booth location data if available, otherwise fallback to locationId
			const newTimeSlots = generateTimeSlots(
				duration,
				boothLocationData || locationId,
				isToday
			);
			setAvailableTimeSlots(newTimeSlots);

			// If currently selected time is no longer available, reset selection
			// But keep "now" selection intact
			if (time && time !== "now" && !newTimeSlots.includes(time)) {
				setTime("");
			}
		};

		// Run immediately on mount
		updateTimeSlots();

		// Then set interval to check every minute
		const intervalId = setInterval(updateTimeSlots, 60000);

		// Clean up interval on unmount
		return () => clearInterval(intervalId);
	}, [duration, locationId, boothLocationData, time, isToday]);

	// Fetch booth availability when booth, duration, or date changes
	useEffect(() => {
		fetchBoothAvailability();

		// Set interval to refresh availability every 30 seconds
		const availabilityInterval = setInterval(fetchBoothAvailability, 30000);

		return () => clearInterval(availabilityInterval);
	}, [boothId, locationId, duration, isToday, fetchBoothAvailability]);

	const continueBtn = () => {
		let startTime = time;
		if (time === "now") startTime = timeNow(boothLocationData || locationId);

		// If we have a pre-selected booth (from QR code), go to booth confirmation
		// Otherwise, go to booth selection
		const targetRoute = preSelectedBooth
			? `/${locationId}/reservation/booth-confirmation`
			: `/${locationId}/reservation/select-box`;

		navigate(targetRoute, {
			state: {
				startTime,
				duration,
				date: isToday ? 0 : 1,
				boothId: boothId,
				...(preSelectedBooth && { preSelectedBooth }),
			},
		});
	};

	return (
		<section
			className={`flex flex-col justify-center bg-white
                  md:px-mdPagePadding md:mx-smPagePadding
                  lg:px-lgPagePadding lg:mx-lgPagePadding xl:px-xlPagePadding xl:mx-xlPagePadding`}
		>
			{/* -- Progress Stepper -- */}
			<div className="mt-10">
				<ProgressStepper currentStep={1} />
			</div>
			{/* -- Logo -- */}
			<div className="flex flex-col justify-center items-center px-2 py-8">
				<img src={logo} alt="logo" className="w-40 h-auto object-contain" />
			</div>

			{/* -- Today/Tomorrow Switch -- */}
			<div className="mx-smPagePadding md:mx-mdPagePadding xl:mx-lgPagePadding">
				<div className="relative flex items-center justify-center bg-gray-100 p-1 rounded-secondary w-full mx-auto">
					<div className="relative z-0 flex w-full">
						{/* Background pill that moves */}
						<div
							className={`absolute top-0 left-0 h-full w-1/2 bg-white rounded-secondary shadow-sm transition-transform duration-200 ease-in-out ${
								isToday ? "translate-x-0" : "translate-x-full"
							}`}
						/>

						{/* Today button */}
						<button
							className={`relative z-10 flex-1 py-2 px-4 text-center text-sm rounded-secondary transition-colors duration-200
                flex items-center justify-center gap-2
                ${isToday ? "text-black font-medium" : "text-gray-500"}
              `}
							onClick={() => setIsToday(true)}
						>
							Today
						</button>

						{/* Tomorrow button */}
						<button
							className={`relative z-10 flex-1 py-2 px-4 text-center text-sm rounded-secondary transition-colors duration-200
                flex items-center justify-center gap-2
                ${!isToday ? "text-black font-medium" : "text-gray-500"}
              `}
							onClick={() => setIsToday(false)}
						>
							Tomorrow
						</button>
					</div>
				</div>
			</div>

			{/* -- Duration -- */}
			<div className="flex justify-center items-center gap-6 mt-8 font-inter border-b border-gray-200 py-4 m-5 border-t">
				{durations.map((durationValue) => (
					<label key={durationValue} className="cursor-pointer">
						<input
							type="radio"
							name="duration"
							value={durationValue}
							className="peer hidden"
							checked={duration === durationValue.toString()}
							onChange={(el) => setDuration(el.target.value)}
						/>
						<span className="peer-checked:text-black peer-checked:font-medium text-gray-400 hover:text-black">
							{durationValue} min
						</span>
					</label>
				))}
			</div>
			{isLoadingAvailability && (
				<div className="flex justify-center items-center py-8">
					<div className="w-6 h-6 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin mr-3"></div>
					<span className="text-gray-600">Loading available times...</span>
				</div>
			)}
			{/* -- Start Now -- */}
			{isToday && (
				<div className="flex justify-center mt-2 px-4">
					<button
						className={`w-full max-w-md px-2 py-4 border rounded-primary font-inter ${
							!startNowAvailable
								? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
								: time === "now"
								? "border-black text-black font-medium"
								: "border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm"
						}`}
						onClick={() => startNowAvailable && setTime("now")}
						disabled={!startNowAvailable}
						title={!startNowAvailable ? "This time slot is not available" : ""}
					>
						{isLoadingAvailability ? "Checking availability..." : "Start now"}
						{!startNowAvailable && " (Unavailable)"}
					</button>
				</div>
			)}
			{/* -- Time Slots -- */}
			<div>
				<div
					className="grid  grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 px-4 mt-6"
					style={{ marginBottom: "130px" }}
				>
					{availableTimeSlots.map((timeSlot) => {
						const isAvailable = realTimeAvailableSlots.includes(timeSlot);
						return (
							<button
								key={timeSlot}
								className={`px-4 py-3 border rounded-secondary font-inter ${
									!isAvailable
										? "border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50"
										: time === timeSlot
										? "border-black text-black font-medium"
										: "border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm"
								}`}
								onClick={() => isAvailable && setTime(timeSlot)}
								disabled={!isAvailable}
								title={!isAvailable ? "This time slot is not available" : ""}
							>
								{timeSlot}
								{!isAvailable && (
									<div className="text-xs text-gray-400 mt-1">Unavailable</div>
								)}
							</button>
						);
					})}
				</div>
			</div>
			<ContinueBtn
				btnBack={false}
				disabled={!time || !duration}
				onClick={continueBtn}
			/>
		</section>
	);
}
